(() => {
  const initialised = new WeakMap();
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const radians = (degrees) => degrees * Math.PI / 180;

  function readJson(root, selector, fallback) {
    try { return JSON.parse(root.querySelector(selector)?.textContent || ''); } catch { return fallback; }
  }

  function sessionToken() {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, (character) => ({ '+': '-', '/': '_', '=': '' })[character]);
  }

  function debounce(callback, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), delay);
    };
  }

  function createElement(tag, attributes = {}, text = '') {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value == null || value === '') return;
      if (key === 'class') element.className = value;
      else element.setAttribute(key, value);
    });
    if (text) element.textContent = text;
    return element;
  }

  // Mirrors snippets/mm-chevron.liquid's default (right-pointing) output
  // exactly, so every chevron this file draws is the system's one chevron.
  function createChevron() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2.4');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M9 6l6 6-6 6');
    svg.append(path);
    return svg;
  }

  // Same conventions as createChevron: 24x24 box, currentColor stroke, round
  // joins, hidden from the accessibility tree because the button carries the
  // label. Icon-only controls need an aria-label AND a title: the title is
  // what a sighted mouse user gets, since there is no longer any text.
  function createIcon(paths) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.9');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    paths.forEach((d) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      svg.append(path);
    });
    return svg;
  }

  // Arrow leaving a tray (the share glyph people know from iOS), two stacked
  // sheets for copy, and a tick for the copied confirmation.
  const ICON_SHARE = ['M12 15.5V4', 'M8.5 7.5 12 4l3.5 3.5', 'M5 12.5v5.9A1.6 1.6 0 0 0 6.6 20h10.8a1.6 1.6 0 0 0 1.6-1.6v-5.9'];
  const ICON_COPY = ['M9.4 9.4h8.9a1.6 1.6 0 0 1 1.6 1.6v8.9a1.6 1.6 0 0 1-1.6 1.6H9.4a1.6 1.6 0 0 1-1.6-1.6V11a1.6 1.6 0 0 1 1.6-1.6z', 'M5.7 15.2h-.6a1.6 1.6 0 0 1-1.6-1.6V4.7a1.6 1.6 0 0 1 1.6-1.6h8.9a1.6 1.6 0 0 1 1.6 1.6v.6'];
  const ICON_TICK = ['M5 12.6 9.6 17 19 7.5'];

  function enhancePill(control, variant = 'nav', options = {}) {
    if (!control || control.classList.contains('mm-btn')) return control;
    const labelText = control.textContent.trim();
    control.replaceChildren();
    control.classList.remove('mm-world__button--light', 'mm-world__button--dark');
    control.classList.add('mm-btn', `mm-btn--${variant}`);
    if (options.onDark) control.classList.add('mm-btn--on-dark');
    if (options.size) control.classList.add(`mm-btn--${options.size}`);
    if (options.wide) control.classList.add('mm-btn--wide');
    if (options.noArrow) control.classList.add('mm-btn--noarrow');
    const ink = createElement('span', { class: 'mm-btn__ink', 'aria-hidden': 'true' });
    const label = createElement('span', { class: 'mm-btn__label' }, labelText);
    control.append(ink, label);
    if (!options.noArrow) {
      const badge = createElement('span', { class: 'mm-btn__badge', 'aria-hidden': 'true' });
      badge.append(createChevron(), createChevron());
      control.append(badge);
    }
    return control;
  }

  function enhanceIcon(control) {
    if (!control || control.classList.contains('mm-ctrl-ico')) return;
    control.classList.add('mm-ctrl-ico');
    const ink = createElement('span', { class: 'mm-ctrl-ico__ink', 'aria-hidden': 'true' });
    if (!control.matches('[data-mm-pause]')) {
      const glyph = createElement('span', { class: 'mm-ctrl-ico__glyph', 'aria-hidden': 'true' });
      glyph.append(...control.childNodes);
      control.append(ink, glyph);
    } else control.prepend(ink);
  }

  function setupControlSystem(root) {
    // Static pill controls (open-submit x2, submit, back-to-globe) now render
    // server-side via {% render 'mm-button' %} as real .mm-btn markup, so the
    // legacy .mm-world__button enhancePill calls that used to progressively
    // enhance them are gone: that class no longer appears in the rendered
    // section at all. enhancePill() itself is kept below for the one control
    // this JS still builds at runtime (the sighting-detail product link).
    root.querySelectorAll('.mm-world__zoom-controls button, .mm-world__pause, .mm-world__dialog-close').forEach(enhanceIcon);
  }

  function setupShareMoments(root, cleanup) {
    const section = root.querySelector('[data-mm-share-moments]');
    const moments = Array.from(root.querySelectorAll('[data-mm-share-moment]'));
    const index = root.querySelector('[data-mm-share-index]');
    if (!section || !moments.length || !index || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      moments.forEach((moment) => moment.classList.toggle('is-active', moment === visible.target));
      index.textContent = visible.target.dataset.mmStep || '01';
    }, { threshold: [.3, .5, .7], rootMargin: '-14% 0px -20% 0px' });

    moments.forEach((moment) => observer.observe(moment));
    cleanup.push(() => observer.disconnect());
  }

  function showDialog(dialog, trigger) {
    if (!dialog) return;
    dialog._mmTrigger = trigger || document.activeElement;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    dialog._mmTrigger?.focus?.();
  }

  // Matches the path sections/mm-world-photo.liquid's page renders at
  // (world_photo metaobject onlineStore urlHandle), so setupDetails can tell
  // a pushed/popped photo URL apart from any other route.
  // Shopify metaobject pages live UNDER /pages/. A urlHandle of "world-photos"
  // produces /pages/world-photos/{entry-handle}, not /world-photos/{entry-handle}.
  // Getting this wrong 404s every shared link while the address bar still looks
  // correct, because pushState never asks the server.
  const WORLD_PHOTO_PATH = /^\/pages\/world-photos\/([^/?#]+)\/?$/;

  function setupDetails(root, sightings, i18n, cleanup) {
    const dialog = root.querySelector('[data-mm-detail-dialog]');
    const body = root.querySelector('[data-mm-detail]');
    const byHandle = new Map(sightings.map((sighting) => [sighting.handle, sighting]));
    // True only while the CURRENT dialog state owns a history entry this
    // tab pushed itself, so close() knows whether there is anything to
    // unwind with history.back() (a popstate-driven open owns none: the
    // browser already did the moving).
    let ownsHistoryEntry = false;

    // Metaobject pages are served by the PUBLISHED theme's metaobject
    // template, so these URLs only resolve while a theme carrying
    // templates/metaobject/world_photo.json is live. The fallback below hands
    // out a hub-page URL instead, which resolves on any theme. One helper
    // feeds the copy button, pushState and popstate, so the three can never
    // disagree about what a photo's URL is.
    const usePermalinks = root.dataset.photoPermalinks === 'true';
    const HUB_PATH = WORLD_PHOTO_PATH.test(window.location.pathname)
      ? '/pages/around-the-world'
      : window.location.pathname;
    const photoPathFor = (handle) => (usePermalinks
      ? `/pages/world-photos/${handle}`
      : `${HUB_PATH}?photo=${encodeURIComponent(handle)}`);
    const handleFromLocation = () => {
      const match = window.location.pathname.match(WORLD_PHOTO_PATH);
      if (match) return match[1];
      return new URLSearchParams(window.location.search).get('photo');
    };

    const open = (handle, trigger, options = {}) => {
      const sighting = byHandle.get(handle);
      if (!sighting || !body) return;
      body.replaceChildren();

      const shell = createElement('div', { class: 'mm-world__spot-detail' });
      const media = createElement('div', { class: 'mm-world__spot-media' });
      // The media pane is full width on phones and about half the dialog on
      // desktop, so let the browser pick rather than always pulling the 1200.
      if (sighting.image) media.append(createElement('img', {
        src: sighting.image,
        srcset: sighting.imageSrcset || '',
        sizes: '(max-width: 899px) 100vw, 50vw',
        alt: sighting.imageAlt || '',
        width: '900',
        height: '1200'
      }));
      media.append(createElement('span', { class: 'mm-world__spot-platform' }, sighting.sourceType === 'social_post' ? `${String(sighting.platform || 'social').replace('_', ' ')} ${i18n.post}` : i18n.teamPhoto));

      // Share keeps sharing the ORIGINAL social post link (unchanged).
      // Copy link is a separate control for THIS dialog's own shareable
      // /pages/world-photos/{handle} URL -- see the pushState block below.
      const actions = createElement('div', { class: 'mm-world__spot-actions' });
      // Icon-only, so the confirmation cannot be a label swap any more. The
      // icon flips to a tick for sighted users and this live region carries
      // the same news to a screen reader.
      const actionStatus = createElement('span', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
      const flashCopied = (button, icon, label) => {
        button.replaceChildren(createIcon(ICON_TICK));
        button.setAttribute('aria-label', i18n.linkCopied);
        button.setAttribute('title', i18n.linkCopied);
        actionStatus.textContent = i18n.linkCopied;
        setTimeout(() => {
          button.replaceChildren(createIcon(icon));
          button.setAttribute('aria-label', label);
          button.setAttribute('title', label);
          actionStatus.textContent = '';
        }, 2000);
      };

      const sharePost = createElement('button', { class: 'mm-world__spot-action', type: 'button', 'aria-label': i18n.shareThis, title: i18n.shareThis });
      sharePost.append(createIcon(ICON_SHARE));
      sharePost.addEventListener('click', async () => {
        const shareData = { title: sighting.title, text: `${sighting.title} in ${sighting.location}`, url: sighting.url || window.location.href };
        try {
          if (navigator.share) await navigator.share(shareData);
          else {
            await navigator.clipboard.writeText(shareData.url);
            flashCopied(sharePost, ICON_SHARE, i18n.shareThis);
          }
        } catch { /* sharing can be cancelled */ }
      });

      const copyLink = createElement('button', { class: 'mm-world__spot-action', type: 'button', 'aria-label': i18n.copyLink, title: i18n.copyLink });
      copyLink.append(createIcon(ICON_COPY));
      copyLink.addEventListener('click', async () => {
        const photoUrl = `${window.location.origin}${photoPathFor(sighting.handle)}`;
        try {
          await navigator.clipboard.writeText(photoUrl);
          flashCopied(copyLink, ICON_COPY, i18n.copyLink);
        } catch { /* clipboard can be blocked */ }
      });

      actions.append(sharePost, copyLink, actionStatus);
      media.append(actions);

      const platformName = String(sighting.platform || 'social').replace('_', ' ').replace(/^./, (character) => character.toUpperCase());
      // mm-tlink supplies colour/underline/chevron-travel; it carries no
      // position of its own, so the placement over the dark photo (formerly
      // owned by the deleted .mm-world__spot-media-action rule) is set here
      // inline rather than reopening the CSS file this wave doesn't own.
      const mediaAction = createElement('a', {
        class: 'mm-tlink mm-tlink--on-dark',
        href: sighting.url,
        target: '_blank',
        rel: 'noopener noreferrer',
        style: 'position:absolute;left:clamp(16px,4vw,28px);bottom:clamp(16px,4vw,28px);z-index:2'
      });
      mediaAction.append(
        createElement('span', {}, sighting.sourceType === 'social_post' ? `${i18n.viewOriginalOn} ${platformName}` : i18n.profile),
        createChevron()
      );
      media.append(mediaAction);

      const copy = createElement('div', { class: 'mm-world__spot-copy' });
      const location = [sighting.location, sighting.continent].filter(Boolean).join(' · ');
      copy.append(createElement('p', { class: 'mm-world__spot-location' }, location));
      copy.append(createElement('h2', { id: dialog.getAttribute('aria-labelledby') }, sighting.author || sighting.title));
      if (sighting.takenOn) {
        const date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${sighting.takenOn}T12:00:00Z`));
        const dateLine = createElement('p', { class: 'mm-world__spot-date' }, `${i18n.takenOn} ${date}`);
        copy.append(dateLine);
      }
      if (sighting.featured) copy.append(createElement('span', { class: 'mm-world__spot-featured' }, i18n.featuredPhoto));
      if (sighting.caption) copy.append(createElement('blockquote', { class: 'mm-world__spot-quote' }, sighting.caption));

      // Product CTA card, per the owner-confirmed reference: image left
      // (fixed square, omitted gracefully when the data has none), eyebrow +
      // name + reward sentence + a real mm-btn--buy stacked on the right,
      // inside the same deep-green panel that used to hold only the reward
      // prompt. Falls back to the 250 ml Original when the sighting has no
      // tagged_product. productImage is being added to data-mm-sightings by
      // the section-owning agent (tagged product's featured image, falling
      // back to the 250 ml Original's) -- consumed here defensively so the
      // card renders image-less and undamaged until that push lands.
      const productTitle = sighting.productTitle || 'Muscle Mint Menthol Cream';
      const productImageSrc = sighting.productImage || '';

      const reward = createElement('div', { class: 'mm-world__spot-reward' });
      const rewardCard = createElement('div', { class: 'mm-world__spot-reward-card' });
      if (productImageSrc) {
        rewardCard.append(createElement('img', {
          class: 'mm-world__spot-reward-image', src: productImageSrc, alt: ''
        }));
      }
      const rewardCopy = createElement('div', { class: 'mm-world__spot-reward-copy' });
      // i18n.rewardHeading ("Taken a Muscle Mint photo too?") is the only
      // existing key that fits this eyebrow's context; text-transform does
      // the visual capitalising so the source string stays natural case for
      // anyone reading it un-styled. No new locale key was added.
      rewardCopy.append(createElement('span', { class: 'mm-world__spot-reward-eyebrow' }, i18n.rewardHeading));
      rewardCopy.append(createElement('strong', { class: 'mm-world__spot-reward-name' }, productTitle));
      rewardCopy.append(createElement('small', { class: 'mm-world__spot-reward-line' }, i18n.rewardBody));
      // This card's copy is an invitation to send a photo in, so the button has
      // to be that action, not "Shop Muscle Mint". It previously sent people to
      // the product page, which contradicted the eyebrow and body text above it
      // and did not match the dedicated photo page, whose button already reads
      // i18n.share and opens the form.
      //
      // Rather than duplicate the submit-dialog wiring here, close this dialog
      // and click the section's existing [data-mm-open-submit] control. That
      // reuses the handler bound at init, so there is still one opener.
      const rewardButton = createElement('button', { type: 'button' }, i18n.share);
      rewardButton.addEventListener('click', () => {
        closeDialog(dialog);
        root.querySelector('[data-mm-open-submit]')?.click();
      });
      enhancePill(rewardButton, 'buy', { size: 'sm' });
      rewardCopy.append(rewardButton);
      rewardCard.append(rewardCopy);
      reward.append(rewardCard);
      copy.append(reward);
      shell.append(media, copy);
      body.append(shell);
      showDialog(dialog, trigger);

      // Give the open photo a copyable URL without a real navigation:
      // pushState only, never replaceState, so the browser's own Back
      // genuinely closes the dialog instead of skipping past it. Skipped
      // when this open() call is itself the RESULT of a popstate (the
      // browser already moved the address bar there) or when it's already
      // showing this exact photo (re-opening the same handle).
      if (!options.fromPopstate) {
        const photoPath = photoPathFor(handle);
        if (`${window.location.pathname}${window.location.search}` !== photoPath) {
          history.pushState({ mmWorldPhoto: handle }, '', photoPath);
          ownsHistoryEntry = true;
        }
      }
    };

    const close = (options = {}) => {
      closeDialog(dialog);
      if (ownsHistoryEntry && !options.fromPopstate) history.back();
      ownsHistoryEntry = false;
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-mm-open]');
      if (trigger && root.contains(trigger)) open(trigger.dataset.mmOpen, trigger);
      if (event.target.closest('[data-mm-close-detail]')) close();
    };
    root.addEventListener('click', onClick);
    const backdropClose = (event) => { if (event.target === dialog) close(); };
    dialog?.addEventListener('click', backdropClose);

    // Browser back/forward: land on a known /pages/world-photos/{handle} path ->
    // open that photo (without re-pushing -- the browser already moved us
    // there). Land anywhere else while the dialog is open -> just close it;
    // never call history.back()/forward() here, that would fight the
    // navigation the browser just performed.
    const onPopstate = () => {
      const handle = handleFromLocation();
      if (handle && byHandle.has(handle)) {
        ownsHistoryEntry = true;
        open(handle, null, { fromPopstate: true });
      } else if (dialog?.open) {
        ownsHistoryEntry = false;
        closeDialog(dialog);
      }
    };
    window.addEventListener('popstate', onPopstate);

    // Someone arriving on a shared link: open that photo straight away.
    // fromPopstate is set so this does not push a duplicate history entry
    // over the one the browser already has, and so closing does not try to
    // go back past the page they landed on.
    const initialHandle = handleFromLocation();
    if (initialHandle && byHandle.has(initialHandle)) {
      open(initialHandle, null, { fromPopstate: true });
    }

    cleanup.push(
      () => root.removeEventListener('click', onClick),
      () => dialog?.removeEventListener('click', backdropClose),
      () => window.removeEventListener('popstate', onPopstate)
    );
    return open;
  }

  function ensureSightingCards(root, sightings, i18n) {
    const grid = root.querySelector('[data-mm-grid]');
    if (!grid) return;
    const cardsByHandle = new Map([...grid.querySelectorAll('[data-mm-card]')].map((card) => [card.querySelector('[data-mm-open]')?.dataset.mmOpen, card]));
    const ordered = [...sightings].sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)) || Number(b.sortPriority || 0) - Number(a.sortPriority || 0) || String(b.takenOn || '').localeCompare(String(a.takenOn || '')));
    ordered.forEach((sighting) => {
      let card = cardsByHandle.get(sighting.handle);
      if (!card) {
        card = createElement('article', {
          class: 'mm-world__card',
          'data-mm-card': 'true',
          'data-search': [sighting.location, sighting.country, sighting.author, sighting.platform, sighting.title].filter(Boolean).join(' ').toLocaleLowerCase('en-GB'),
          'data-platform': sighting.platform || 'muscle_mint',
          'data-photo-type': sighting.photoType || 'own_bottle',
          'data-featured': String(Boolean(sighting.featured))
        });
        const button = createElement('button', { type: 'button', class: 'mm-world__card-button', 'data-mm-open': sighting.handle });
        const media = createElement('span', { class: 'mm-world__card-media' });
        const image = createElement('img', { src: sighting.image, width: '1200', height: '900', loading: 'lazy', alt: sighting.imageAlt || `${sighting.location} ${i18n.photoImage}` });
        const platformName = String(sighting.platform || 'muscle_mint').replace('_', ' ').replace(/^./, (character) => character.toUpperCase());
        media.append(image, createElement('span', { class: 'mm-world__platform' }, platformName));
        if (sighting.featured) media.append(createElement('span', { class: 'mm-world__featured' }, i18n.featured));
        const copy = createElement('span', { class: 'mm-world__card-copy' });
        copy.append(
          createElement('span', { class: 'mm-world__location' }, sighting.location),
          createElement('strong', {}, sighting.title),
          createElement('span', {}, sighting.author || '@musclemintco')
        );
        button.append(media, copy);
        card.append(button);
        cardsByHandle.set(sighting.handle, card);
      }
      grid.append(card);
    });
  }

  function setupFilters(root, i18n, cleanup) {
    const form = root.querySelector('[data-mm-filters]');
    if (!form) return;
    const cards = [...root.querySelectorAll('[data-mm-card]')];
    const search = form.querySelector('[data-mm-search]');
    const platform = form.querySelector('[data-mm-platform]');
    const photoType = form.querySelector('[data-mm-photo-type]');
    const featured = form.querySelector('[data-mm-featured]');
    const count = form.querySelector('[data-mm-count]');
    const empty = root.querySelector('[data-mm-empty]');
    const loadMore = root.querySelector('[data-mm-load-more]');
    // Sits below the button, not inside its label, so it keeps its own
    // subdued colour instead of inheriting the button's light-on-dark text.
    const remaining = root.querySelector('[data-mm-remaining]');
    const initialVisible = 3;
    const pageSize = 6;
    let revealLimit = initialVisible;
    const apply = () => {
      const query = search.value.trim().toLocaleLowerCase('en-GB');
      const filtersActive = Boolean(query || platform.value || photoType.value || featured.checked);
      const matches = [];
      cards.forEach((card) => {
        const cardMatches = (!query || card.dataset.search.includes(query))
          && (!platform.value || card.dataset.platform === platform.value)
          && (!photoType.value || card.dataset.photoType === photoType.value)
          && (!featured.checked || card.dataset.featured === 'true');
        if (cardMatches) matches.push(card);
        else card.hidden = true;
      });
      const visibleLimit = filtersActive ? matches.length : revealLimit;
      matches.forEach((card, index) => { card.hidden = index >= visibleLimit; });
      const visible = Math.min(matches.length, visibleLimit);
      count.textContent = filtersActive
        ? `${visible} ${visible === 1 ? i18n.photo : i18n.photos}`
        : `${i18n.showing} ${visible} ${i18n.of} ${matches.length} ${i18n.photos}`;
      if (empty) empty.hidden = matches.length > 0 || cards.length === 0;
      if (loadMore) loadMore.hidden = filtersActive || visible >= matches.length;
      if (remaining) {
        const left = Math.max(0, matches.length - visible);
        remaining.textContent = `${left} ${i18n.remaining}`;
        remaining.hidden = !loadMore || loadMore.hidden || left === 0;
      }
    };
    const reset = () => { revealLimit = initialVisible; requestAnimationFrame(apply); };
    const revealMore = () => { revealLimit += pageSize; apply(); };
    form.addEventListener('input', apply);
    form.addEventListener('reset', reset);
    loadMore?.addEventListener('click', revealMore);
    cleanup.push(
      () => form.removeEventListener('input', apply),
      () => form.removeEventListener('reset', reset),
      () => loadMore?.removeEventListener('click', revealMore)
    );
    apply();
  }

  function setupGlobe(root, sightings, openDetail, i18n, cleanup) {
    const stage = root.querySelector('[data-mm-stage]');
    const canvas = root.querySelector('[data-mm-globe]');
    const layer = root.querySelector('[data-mm-pin-layer]');
    const pause = root.querySelector('[data-mm-pause]');
    const zoomIn = root.querySelector('[data-mm-zoom-in]');
    const zoomOut = root.querySelector('[data-mm-zoom-out]');
    const zoomReset = root.querySelector('[data-mm-zoom-reset]');
    const viewReset = root.querySelector('[data-mm-view-reset]');
    const zoomLevel = root.querySelector('[data-mm-zoom-level]');
    if (!stage || !canvas || !layer) return;
    const context = canvas.getContext('2d', { alpha: true });
    const metrics = canvas._mmMetrics = { drawCount: 0, lastDrawMs: 0, maxDrawMs: 0 };
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const MIN_ZOOM = 0.8;
    const MAX_ZOOM = 16;
    const initialView = { yaw: 0, pitch: 15, zoom: 1 };
    // Degrees per millisecond. Previously degrees per frame (0.0092 idle,
    // 0.0207 initial) which assumed 60Hz; these are those values converted to
    // a time base and raised 20%, so the globe now settles at 0.662 deg/sec
    // (about nine minutes a revolution) on every device rather than only on a
    // 60Hz one.
    const IDLE_SPIN = 0.0006624;
    const INITIAL_SPIN = 0.0014904;
    const state = { yaw: initialView.yaw, pitch: initialView.pitch, zoom: initialView.zoom, velocity: INITIAL_SPIN, lastTickAt: 0, dragging: false, pinching: false, pointerX: 0, pointerY: 0, pointerStartX: 0, pointerStartY: 0, pointerMoved: false, lastX: 0, lastTime: 0, lastTapAt: 0, lastTapX: 0, lastTapY: 0, pinchDistance: 0, pinchZoom: 1, lastDrawAt: 0, paused: reducedMotion.matches, visible: true, frame: 0, width: 0, height: 0, baseRadius: 0, radius: 0 };
    canvas.dataset.gestureSupport = 'drag pinch double-tap wheel controls';
    // Base layer: a simplified coastline (172 lines / 2,969 points, 15KB
    // gzipped) drawn at the default zoom, replacing the dot cloud that used to
    // be sampled out of a land-mask image. Dots sample a landmass by AREA, an
    // outline samples it by PERIMETER, so an outline needs roughly the square
    // root of the points for the same read -- and each point is one lineTo
    // vertex rather than an arc(), which the canvas tessellates into about a
    // dozen. Net: ~2,969 vertices a frame instead of ~70,000, and the whole
    // fetch/decode/getImageData land-mask pipeline disappears from page load.
    let baseOutline = [];
    let boundaries = { coastlines: [], countries: [], states: [] };
    let detailLoadPromise;
    const pinButtons = sightings.map((sighting) => {
      const button = createElement('button', { type: 'button', class: 'mm-world__globe-pin', 'aria-label': `${sighting.title}, ${sighting.location}` });
      button.dataset.handle = sighting.handle;
      const tooltip = createElement('span', { class: 'mm-world__pin-tooltip', 'aria-hidden': 'true' });
      tooltip.append(createElement('strong', {}, sighting.location));
      const platformName = String(sighting.platform || 'Muscle Mint').replace('_', ' ').replace(/^./, (character) => character.toUpperCase());
      tooltip.append(createElement('small', {}, `${platformName} · ${sighting.author || '@musclemintco'}`));
      button.append(tooltip);
      button.addEventListener('click', () => openDetail(sighting.handle, button));
      layer.append(button);
      return { button, sighting };
    });
    pinButtons[0]?.button.classList.add('is-active');

    const clusterButtons = [];
    const pinByHandle = new Map(pinButtons.map((item) => [item.sighting.handle, item]));
    let clusterPanelOrigin = null;
    const clusterPanel = createElement('section', {
      class: 'mm-world__cluster-panel',
      role: 'dialog',
      'aria-modal': 'false',
      'aria-label': i18n.chooseNearby
    });
    clusterPanel.hidden = true;
    stage.append(clusterPanel);

    const closeClusterPanel = ({ restoreFocus = true } = {}) => {
      clusterPanel.hidden = true;
      clusterPanel.replaceChildren();
      if (restoreFocus) clusterPanelOrigin?.focus();
      clusterPanelOrigin = null;
    };

    const openClusterPanel = (items, origin) => {
      clusterPanelOrigin = origin;
      clusterPanel.replaceChildren();
      const heading = createElement('div', { class: 'mm-world__cluster-heading' });
      const headingCopy = createElement('div');
      headingCopy.append(
        createElement('strong', {}, `${items.length} ${i18n.nearbyPhotos}`),
        createElement('span', {}, i18n.choosePhoto)
      );
      const close = createElement('button', { type: 'button', class: 'mm-world__cluster-close', 'aria-label': i18n.closeNearby }, '×');
      close.addEventListener('click', () => closeClusterPanel());
      heading.append(headingCopy, close);
      const list = createElement('div', { class: 'mm-world__cluster-list' });
      items.forEach(({ sighting }) => {
        const choice = createElement('button', { type: 'button', class: 'mm-world__cluster-choice' });
        // 72x88 on screen, so take the 176 wide derivative, not the 1200.
        const image = createElement('img', { src: sighting.imageThumb || sighting.image, alt: '', loading: 'lazy', width: '72', height: '88' });
        image.alt = '';
        const copy = createElement('span');
        copy.append(createElement('strong', {}, sighting.location), createElement('small', {}, sighting.author || '@musclemintco'));
        choice.append(image, copy);
        choice.addEventListener('click', () => {
          const detailOrigin = clusterPanelOrigin || choice;
          closeClusterPanel({ restoreFocus: false });
          openDetail(sighting.handle, detailOrigin);
        });
        list.append(choice);
      });
      clusterPanel.append(heading, list);
      clusterPanel.hidden = false;
      close.focus();
    };

    const sphericalPoint = (latitude, longitude) => {
      const lat = radians(latitude);
      const lon = radians(longitude);
      return { sinLat: Math.sin(lat), cosLat: Math.cos(lat), sinLon: Math.sin(lon), cosLon: Math.cos(lon) };
    };

    const projectionTransform = () => {
      const yaw = radians(state.yaw);
      const pitch = radians(state.pitch);
      return { cosYaw: Math.cos(yaw), sinYaw: Math.sin(yaw), cosPitch: Math.cos(pitch), sinPitch: Math.sin(pitch) };
    };

    const projectPoint = (point, transform = projectionTransform()) => {
      const sinLon = point.sinLon * transform.cosYaw + point.cosLon * transform.sinYaw;
      const cosLon = point.cosLon * transform.cosYaw - point.sinLon * transform.sinYaw;
      const baseY = point.sinLat;
      const baseZ = point.cosLat * cosLon;
      const rotatedY = baseY * transform.cosPitch - baseZ * transform.sinPitch;
      const rotatedZ = baseY * transform.sinPitch + baseZ * transform.cosPitch;
      return {
        x: state.width / 2 + state.radius * point.cosLat * sinLon,
        y: state.height / 2 - state.radius * rotatedY,
        z: rotatedZ
      };
    };
    const project = (latitude, longitude) => projectPoint(sphericalPoint(latitude, longitude));

    // Loads the simplified coastline used as the base layer. Replaces the old
    // land-mask pipeline, which fetched a WebP, decoded it via
    // createImageBitmap, drew it to a 720x360 scratch canvas and then scanned
    // all 259,200 pixels to build a dot cloud -- all on the main thread at
    // load. This is a 15KB gzipped fetch and a JSON.parse instead.
    //
    // Coordinates are GeoJSON order, [longitude, latitude], matching the
    // boundaries payload and its prepare() below. Getting that backwards
    // silently renders a transposed globe that still looks vaguely like land,
    // so the order is asserted by the shape of the data, not assumed.
    const loadBaseOutline = async () => {
      if (!root.dataset.outline) return [];
      try {
        const response = await fetch(root.dataset.outline);
        if (!response.ok) throw new Error(`Outline fetch failed: ${response.status}`);
        const payload = await response.json();
        const lines = payload.outline || [];
        return lines.map((line) => line.map(([longitude, latitude]) => sphericalPoint(latitude, longitude)));
      } catch (error) {
        console.warn('mm-around-the-world: base outline failed to load, falling back to the full boundary set', error);
        return [];
      }
    };

    const ensureDetailBoundaries = () => {
      if (detailLoadPromise || !root.dataset.boundaries) return detailLoadPromise;
      detailLoadPromise = fetch(root.dataset.boundaries)
        .then((response) => {
          if (!response.ok) throw new Error('Boundary data unavailable');
          return response.json();
        })
        .then((payload) => {
          const prepare = (lines) => (lines || []).map((line) => line.map(([longitude, latitude]) => sphericalPoint(latitude, longitude)));
          boundaries = { coastlines: prepare(payload.coastlines), countries: prepare(payload.countries), states: prepare(payload.states) };
          canvas.dataset.coastlineCount = String(boundaries.coastlines.length);
          canvas.dataset.countryBoundaryCount = String(boundaries.countries.length);
          canvas.dataset.stateBoundaryCount = String(boundaries.states.length);
          draw();
        })
        .catch((error) => {
          console.warn('mm-around-the-world: boundaries fetch failed, coastline/country/state overlays disabled', error);
          boundaries = { coastlines: [], countries: [], states: [] };
        });
      return detailLoadPromise;
    };

    const resize = () => {
      const rect = stage.getBoundingClientRect();
      // Cap the backing store harder on phones. At DPR 2 a 349x480 stage is
      // 670k pixels to clear and repaint every frame; 1.5 cuts that by ~44%.
      // The globe is fine dots and hairlines on a flat sphere, so the softening
      // is not visible, and desktop keeps the full 2x.
      const maxRatio = window.matchMedia('(max-width: 749px)').matches ? 1.5 : 2;
      const ratio = Math.min(devicePixelRatio || 1, maxRatio);
      state.width = Math.max(280, rect.width);
      state.height = Math.max(320, rect.height);
      state.baseRadius = Math.min(state.width, state.height) * 0.425;
      state.radius = state.baseRadius * state.zoom;
      canvas.width = Math.round(state.width * ratio);
      canvas.height = Math.round(state.height * ratio);
      canvas.style.width = `${state.width}px`;
      canvas.style.height = `${state.height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const setZoom = (nextZoom) => {
      state.zoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
      state.radius = state.baseRadius * state.zoom;
      pinButtons[0]?.button.classList.toggle('is-active', state.zoom <= 1.02);
      if (zoomLevel) zoomLevel.textContent = `${Math.round(state.zoom * 100)}%`;
      if (zoomIn) zoomIn.disabled = state.zoom >= MAX_ZOOM - 0.001;
      if (zoomOut) zoomOut.disabled = state.zoom <= MIN_ZOOM + 0.001;
      stage.dataset.detail = String(state.zoom > 1.02);
      stage.dataset.zoom = String(Math.round(state.zoom * 100));
      if (state.zoom > 1.02) ensureDetailBoundaries();
      draw();
    };

    const clusterVisiblePins = (items) => {
      const clusterRadius = clamp(50 - Math.log2(Math.max(1, state.zoom)) * 6, 22, 50);
      const clusters = [];
      items.sort((a, b) => b.point.z - a.point.z).forEach((item) => {
        let nearest = null;
        let nearestDistance = Infinity;
        clusters.forEach((cluster) => {
          const distance = Math.hypot(item.point.x - cluster.x, item.point.y - cluster.y);
          if (distance <= clusterRadius && distance < nearestDistance) { nearest = cluster; nearestDistance = distance; }
        });
        if (!nearest) {
          clusters.push({ items: [item], x: item.point.x, y: item.point.y, z: item.point.z });
          return;
        }
        nearest.items.push(item);
        const count = nearest.items.length;
        nearest.x += (item.point.x - nearest.x) / count;
        nearest.y += (item.point.y - nearest.y) / count;
        nearest.z = Math.max(nearest.z, item.point.z);
      });
      return clusters;
    };

    const centreCluster = (items) => {
      const latitude = items.reduce((sum, item) => sum + Number(item.sighting.latitude), 0) / items.length;
      const longitudeVector = items.reduce((vector, item) => {
        const longitude = radians(Number(item.sighting.longitude));
        vector.x += Math.cos(longitude); vector.y += Math.sin(longitude);
        return vector;
      }, { x: 0, y: 0 });
      const longitude = Math.atan2(longitudeVector.y, longitudeVector.x) * 180 / Math.PI;
      state.yaw = -longitude;
      state.pitch = clamp(latitude, -72, 72);
      state.velocity = 0;
    };

    const getClusterButton = (index) => {
      if (clusterButtons[index]) return clusterButtons[index];
      const button = createElement('button', { type: 'button', class: 'mm-world__globe-cluster' });
      const count = createElement('span', { class: 'mm-world__globe-cluster-count', 'aria-hidden': 'true' });
      const hint = createElement('span', { class: 'mm-world__globe-cluster-hint', 'aria-hidden': 'true' });
      button.append(count, hint);
      button.addEventListener('click', () => {
        const items = String(button.dataset.clusterHandles || '').split(',').map((handle) => pinByHandle.get(handle)).filter(Boolean);
        if (!items.length) return;
        if (state.zoom >= MAX_ZOOM - 0.01) {
          stage.dataset.lastClusterAction = 'chooser';
          requestAnimationFrame(() => openClusterPanel(items, button));
          return;
        }
        stage.dataset.lastClusterAction = 'zoom';
        closeClusterPanel({ restoreFocus: false });
        centreCluster(items);
        setZoom(Math.min(MAX_ZOOM, Math.max(state.zoom * 2.2, state.zoom + 0.8)));
      });
      layer.append(button);
      clusterButtons.push(button);
      return button;
    };

    const draw = () => {
      const drawStartedAt = performance.now();
      context.clearRect(0, 0, state.width, state.height);
      const transform = projectionTransform();
      context.save();
      context.shadowColor = 'rgba(35, 92, 67, .11)';
      context.shadowBlur = 46;
      const gradient = context.createRadialGradient(state.width * 0.42, state.height * 0.34, state.radius * 0.04, state.width / 2, state.height / 2, state.radius);
      gradient.addColorStop(0, '#ffffff'); gradient.addColorStop(0.62, '#f4faf6'); gradient.addColorStop(1, '#e8f3ec');
      context.beginPath(); context.arc(state.width / 2, state.height / 2, state.radius, 0, Math.PI * 2); context.fillStyle = gradient; context.fill();
      context.shadowBlur = 0;
      context.strokeStyle = 'rgba(74, 125, 103, .16)'; context.lineWidth = 1.2; context.stroke();
      context.restore();
      context.save();
      context.beginPath(); context.arc(state.width / 2, state.height / 2, state.radius - 1, 0, Math.PI * 2); context.clip();
      const outlinesReady = boundaries.coastlines.length > 0;
      // How far the full-detail coastline has faded in. The simplified base
      // outline fades out over exactly the same range, so the handover is a
      // crossfade rather than a pop. Until the detail set has loaded the base
      // stays fully opaque, which is also the fallback if it never loads.
      const detailFade = outlinesReady ? clamp((state.zoom - 1.02) / 0.22, 0, 1) : 0;
      const baseOutlineOpacity = 1 - detailFade;
      canvas.dataset.baseOutlineOpacity = baseOutlineOpacity.toFixed(2);
      canvas.dataset.activeLayer = state.zoom >= 1.85 ? 'states' : state.zoom >= 1.4 ? 'countries' : state.zoom > 1.02 ? 'coastlines' : 'outline';

      const drawBoundaries = (lines, color, width, dash) => {
        if (!lines.length) return;
        const smooth = state.zoom >= 6;
        canvas.dataset.boundarySmoothing = smooth ? 'quadratic' : 'linear';
        const drawRun = (points) => {
          if (points.length < 2) return;
          context.moveTo(points[0].x, points[0].y);
          if (!smooth || points.length === 2) {
            points.slice(1).forEach((point) => context.lineTo(point.x, point.y));
            return;
          }
          for (let index = 1; index < points.length - 1; index += 1) {
            const point = points[index];
            const next = points[index + 1];
            context.quadraticCurveTo(point.x, point.y, (point.x + next.x) / 2, (point.y + next.y) / 2);
          }
          const last = points[points.length - 1];
          context.lineTo(last.x, last.y);
        };
        context.beginPath();
        lines.forEach((line) => {
          let run = [];
          let previous = null;
          line.forEach((boundaryPoint) => {
            const point = projectPoint(boundaryPoint, transform);
            const onFront = point.z > 0.015 && point.x > -30 && point.x < state.width + 30 && point.y > -30 && point.y < state.height + 30;
            if (!onFront) { drawRun(run); run = []; previous = null; return; }
            const separated = previous && Math.hypot(point.x - previous.x, point.y - previous.y) > state.radius * 0.22;
            if (separated) { drawRun(run); run = []; }
            run.push(point);
            previous = point;
          });
          drawRun(run);
        });
        context.setLineDash(dash);
        context.strokeStyle = color;
        context.lineWidth = width;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.stroke();
        context.setLineDash([]);
      };
      // Base coastline. Same colour family as the detail set so the crossfade
      // is invisible; a touch heavier so continents read at the default zoom,
      // where the globe is only about 300px across on a phone.
      if (baseOutline.length && baseOutlineOpacity > 0.01) {
        drawBoundaries(baseOutline, `rgba(24, 78, 58, ${(0.82 * baseOutlineOpacity).toFixed(3)})`, clamp(state.zoom * 0.8, 1.05, 1.9), []);
      }
      if (outlinesReady) {
        const coastlineOpacity = detailFade * 0.82;
        if (coastlineOpacity > 0) drawBoundaries(boundaries.coastlines, `rgba(24, 78, 58, ${coastlineOpacity})`, clamp(state.zoom * 0.62, 0.82, 1.9), []);
        if (state.zoom >= 1.4) drawBoundaries(boundaries.countries, 'rgba(25, 82, 60, .88)', clamp(state.zoom * 0.58, 0.85, 1.85), [1.4, 1.9]);
        if (state.zoom >= 1.85) drawBoundaries(boundaries.states, 'rgba(42, 101, 77, .62)', clamp(state.zoom * 0.4, 0.65, 1.2), [1, 2.8]);
      }
      context.restore(); context.globalAlpha = 1;

      const visiblePins = [];
      pinButtons.forEach(({ button, sighting }) => {
        const point = projectPoint(sphericalPoint(Number(sighting.latitude), Number(sighting.longitude)), transform);
        const onStage = point.x >= 12 && point.x <= state.width - 12 && point.y >= 12 && point.y <= state.height - 12;
        button.hidden = true;
        if (point.z > 0.04 && onStage) visiblePins.push({ button, sighting, point });
      });
      clusterButtons.forEach((button) => { button.hidden = true; button.dataset.clusterHandles = ''; });
      const clusters = clusterVisiblePins(visiblePins);
      let visibleClusterCount = 0;
      clusters.forEach((cluster) => {
        if (cluster.items.length === 1) {
          const { button, point } = cluster.items[0];
          button.hidden = false;
          button.style.transform = `translate3d(${point.x}px, ${point.y}px, 0) translate(-50%, -50%) scale(${0.78 + point.z * 0.32})`;
          button.style.zIndex = String(Math.round(point.z * 100));
          return;
        }
        const button = getClusterButton(visibleClusterCount++);
        button.hidden = false;
        button.dataset.clusterHandles = cluster.items.map((item) => item.sighting.handle).join(',');
        button.querySelector('.mm-world__globe-cluster-count').textContent = String(cluster.items.length);
        button.querySelector('.mm-world__globe-cluster-hint').textContent = state.zoom >= MAX_ZOOM - 0.01 ? i18n.choosePhotos : i18n.zoomIn;
        button.setAttribute('aria-label', `${cluster.items.length} ${i18n.nearbyPhotos}. ${state.zoom >= MAX_ZOOM - 0.01 ? i18n.openChooser : i18n.zoomSeparate}`);
        button.style.transform = `translate3d(${cluster.x}px, ${cluster.y}px, 0) translate(-50%, -50%)`;
        button.style.zIndex = String(Math.round(cluster.z * 100) + 2);
      });
      layer.dataset.visiblePins = String(clusters.filter((cluster) => cluster.items.length === 1).length);
      layer.dataset.visibleClusters = String(visibleClusterCount);
      metrics.drawCount += 1;
      metrics.lastDrawMs = performance.now() - drawStartedAt;
      metrics.maxDrawMs = Math.max(metrics.maxDrawMs, metrics.lastDrawMs);
      canvas.dataset.drawCount = String(metrics.drawCount);
      canvas.dataset.lastDrawMs = metrics.lastDrawMs.toFixed(2);
      canvas.dataset.maxDrawMs = metrics.maxDrawMs.toFixed(2);
    };

    const tick = (timestamp = 0) => {
      // Rotation is advanced per elapsed millisecond, not per frame. Advancing
      // per frame made the spin speed depend on the display: a 120Hz phone span
      // the globe at twice the rate of a 60Hz one off the same constants.
      // Clamped so a long frame (or the first frame after the loop restarts on
      // scroll-back) cannot jump the globe forward by a visible chunk.
      const elapsed = state.lastTickAt ? Math.min(timestamp - state.lastTickAt, 64) : 0;
      state.lastTickAt = timestamp;
      // Spin only at the default zoom. It used to keep spinning all the way to
      // 3.25x, which fought the visitor for control the moment they zoomed in.
      // Any zoom-in now hands over full manual control; zoom-reset resumes it.
      const spinning = state.visible && !state.paused && !state.dragging && state.zoom <= 1.001;
      if (spinning && elapsed) {
        state.yaw = (state.yaw + state.velocity * elapsed) % 360;
        state.velocity *= Math.pow(0.995, elapsed / 16.667);
        if (Math.abs(state.velocity) < IDLE_SPIN) state.velocity = IDLE_SPIN;
      }
      // Only repaint when the globe can actually be seen. This used to call
      // draw() unconditionally, so a canvas the visitor had already scrolled
      // past kept repainting at up to 66fps and starved the main thread. On a
      // phone that surfaced as a multi-second delay before the search field
      // would accept typing.
      if (state.visible && !state.paused) {
        // 30fps is indistinguishable from 60 for a rotation this slow (about
        // half a degree per second) and halves the work. Dragging is the one
        // case that genuinely needs every frame.
        const frameInterval = state.dragging ? 16 : state.zoom > 3.25 ? 50 : 33;
        if (timestamp - state.lastDrawAt >= frameInterval) { draw(); state.lastDrawAt = timestamp; }
      }
      state.frame = requestAnimationFrame(tick);
    };

    // Off-screen, stop the loop outright rather than running an empty callback
    // 60 times a second. requestAnimationFrame is only throttled by the browser
    // when the whole tab is hidden, not when an element scrolls out of view.
    const startLoop = () => { if (!state.frame) state.frame = requestAnimationFrame(tick); };
    const stopLoop = () => { if (state.frame) { cancelAnimationFrame(state.frame); state.frame = 0; } };

    const pointerDown = (event) => {
      if (event.target.closest('button, a')) return;
      if (state.pinching) return;
      state.dragging = true; state.pointerX = event.clientX; state.pointerY = event.clientY; state.pointerStartX = event.clientX; state.pointerStartY = event.clientY; state.pointerMoved = false; state.lastX = event.clientX; state.lastTime = performance.now();
      stage.setPointerCapture?.(event.pointerId);
    };
    const pointerMove = (event) => {
      if (!state.dragging || state.pinching) return;
      const delta = event.clientX - state.pointerX;
      const deltaY = event.clientY - state.pointerY;
      if (Math.hypot(event.clientX - state.pointerStartX, event.clientY - state.pointerStartY) > 8) state.pointerMoved = true;
      state.pointerX = event.clientX;
      state.pointerY = event.clientY;
      const dragScale = Math.max(1, state.zoom / 2);
      state.yaw += delta * 0.32 / dragScale;
      state.pitch = clamp(state.pitch + deltaY * 0.2 / dragScale, -72, 72);
      const now = performance.now();
      // px/ms of flick -> deg/ms of spin. The 0.24 and the +/-0.048 clamp are
      // the old per-frame 4 and +/-0.8 divided by 16.667, so a flick throws the
      // globe exactly as far as it always did.
      state.velocity = clamp((event.clientX - state.lastX) / Math.max(16, now - state.lastTime) * 0.24, -0.048, 0.048);
      state.lastX = event.clientX; state.lastTime = now;
    };
    const pointerUp = (event) => {
      state.dragging = false;
      if (event.pointerType !== 'touch' || state.pinching || state.pointerMoved || event.target.closest('button, a')) return;
      const now = performance.now();
      const isDoubleTap = now - state.lastTapAt < 320 && Math.hypot(event.clientX - state.lastTapX, event.clientY - state.lastTapY) < 28;
      if (isDoubleTap) {
        event.preventDefault();
        state.lastTapAt = 0;
        setZoom(state.zoom * 1.8);
      } else {
        state.lastTapAt = now; state.lastTapX = event.clientX; state.lastTapY = event.clientY;
      }
    };
    const touchDistance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    const pinchStart = (event) => {
      if (event.touches.length !== 2) return;
      event.preventDefault();
      state.pinching = true; state.dragging = false; state.pointerMoved = true; state.pinchDistance = touchDistance(event.touches); state.pinchZoom = state.zoom;
    };
    const pinchMove = (event) => {
      if (!state.pinching || event.touches.length !== 2) return;
      event.preventDefault();
      const distance = touchDistance(event.touches);
      if (state.pinchDistance > 0) setZoom(state.pinchZoom * distance / state.pinchDistance);
    };
    const pinchEnd = (event) => {
      if (event.touches.length < 2) { state.pinching = false; state.dragging = false; }
    };
    const wheelZoom = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setZoom(state.zoom * Math.exp(-event.deltaY * 0.002));
    };
    const doubleClickZoom = (event) => { event.preventDefault(); setZoom(state.zoom * 1.8); };
    const zoomInClick = () => setZoom(state.zoom * 1.5);
    const zoomOutClick = () => setZoom(state.zoom / 1.5);
    const zoomResetClick = () => setZoom(initialView.zoom);
    const viewResetClick = () => {
      closeClusterPanel({ restoreFocus: false });
      state.yaw = initialView.yaw; state.pitch = initialView.pitch; state.velocity = INITIAL_SPIN;
      state.paused = reducedMotion.matches;
      pause?.setAttribute('aria-pressed', String(state.paused));
      const pauseLabel = pause?.querySelector('[data-mm-pause-label]');
      if (pauseLabel) pauseLabel.textContent = state.paused ? i18n.resume : i18n.pause;
      stage.dataset.lastClusterAction = 'reset';
      setZoom(initialView.zoom);
    };
    const togglePause = () => {
      state.paused = !state.paused;
      pause.setAttribute('aria-pressed', String(state.paused));
      pause.querySelector('[data-mm-pause-label]').textContent = state.paused ? i18n.resume : i18n.pause;
    };
    const visibility = () => { state.visible = !document.hidden; if (state.visible) startLoop(); else stopLoop(); };
    const observer = new IntersectionObserver(([entry]) => {
      state.visible = entry.isIntersecting && !document.hidden;
      if (state.visible) startLoop(); else stopLoop();
      // No rootMargin. A 100px margin kept the globe drawing for another 100px
      // after it left the screen, which on a phone is precisely where the
      // submission form's search field sits.
    }, { rootMargin: '0px' });
    const resizeObserver = new ResizeObserver(resize);
    observer.observe(stage); resizeObserver.observe(stage);
    stage.addEventListener('pointerdown', pointerDown);
    stage.addEventListener('pointermove', pointerMove);
    stage.addEventListener('pointerup', pointerUp);
    stage.addEventListener('pointercancel', pointerUp);
    stage.addEventListener('touchstart', pinchStart, { passive: false });
    stage.addEventListener('touchmove', pinchMove, { passive: false });
    stage.addEventListener('touchend', pinchEnd, { passive: true });
    stage.addEventListener('touchcancel', pinchEnd, { passive: true });
    stage.addEventListener('wheel', wheelZoom, { passive: false });
    stage.addEventListener('dblclick', doubleClickZoom);
    pause?.addEventListener('click', togglePause);
    zoomIn?.addEventListener('click', zoomInClick);
    zoomOut?.addEventListener('click', zoomOutClick);
    zoomReset?.addEventListener('click', zoomResetClick);
    viewReset?.addEventListener('click', viewResetClick);
    document.addEventListener('visibilitychange', visibility);
    resize();
    loadBaseOutline().then((lines) => {
      baseOutline = lines;
      canvas.dataset.baseOutlineLineCount = String(lines.length);
      canvas.dataset.baseOutlinePointCount = String(lines.reduce((total, line) => total + line.length, 0));
      // If the simplified outline could not be fetched, fall back to the full
      // boundary set so the globe is never a blank sphere. That payload is
      // heavy, which is exactly why it is a fallback and not the default.
      if (!lines.length) ensureDetailBoundaries();
      draw();
      // Deliberately NOT preloading the boundary data here. It is
      // 620KB of GeoJSON, and fetching plus JSON.parse plus the prepare()
      // transform all run on the main thread, so an idle-callback preload
      // froze the page for a beat about two seconds after load for every
      // visitor, including the large majority who never zoom in far enough
      // to see a coastline. It still loads on demand the moment zoom passes
      // 1.02, which is the only point at which it is drawn.
    });
    tick();
    cleanup.push(() => {
      cancelAnimationFrame(state.frame); observer.disconnect(); resizeObserver.disconnect();
      stage.removeEventListener('pointerdown', pointerDown); stage.removeEventListener('pointermove', pointerMove);
      stage.removeEventListener('pointerup', pointerUp); stage.removeEventListener('pointercancel', pointerUp);
      stage.removeEventListener('touchstart', pinchStart); stage.removeEventListener('touchmove', pinchMove);
      stage.removeEventListener('touchend', pinchEnd); stage.removeEventListener('touchcancel', pinchEnd);
      stage.removeEventListener('wheel', wheelZoom); stage.removeEventListener('dblclick', doubleClickZoom);
      pause?.removeEventListener('click', togglePause); zoomIn?.removeEventListener('click', zoomInClick);
      zoomOut?.removeEventListener('click', zoomOutClick); zoomReset?.removeEventListener('click', zoomResetClick);
      viewReset?.removeEventListener('click', viewResetClick);
      document.removeEventListener('visibilitychange', visibility);
      pinButtons.forEach(({ button }) => button.remove());
      clusterButtons.forEach((button) => button.remove());
      clusterPanel.remove();
    });
  }

  async function compressImage(file) {
    if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1_000_000) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.82));
      return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }) : file;
    } catch { return file; }
  }

  // Matches the exact string WorldController::submit returns on a Turnstile
  // failure (app/Http/Controllers/Api/V1/WorldController.php). Not localised
  // server-side today, so this is a literal compare; revisit if that ever
  // changes to a machine-readable code.
  const TURNSTILE_FAILURE_MESSAGE = 'Please complete the security check and try again.';

  function setupSubmission(root, i18n, cleanup) {
    const dialog = root.querySelector('[data-mm-submit-dialog]');
    const formHead = dialog?.querySelector('.mm-world__form-head');
    const form = root.querySelector('[data-mm-submit-form]');
    const success = root.querySelector('[data-mm-success]');
    const errors = root.querySelector('[data-mm-form-errors]');
    const placeSearch = root.querySelector('[data-mm-place-search]');
    const placeId = root.querySelector('[data-mm-place-id]');
    const predictions = root.querySelector('[data-mm-predictions]');
    const proxyBase = root.dataset.proxyBase.replace(/\/$/, '');
    let token = sessionToken();
    let abortController;

    const open = (event) => showDialog(dialog, event.currentTarget);
    const close = () => closeDialog(dialog);
    root.querySelectorAll('[data-mm-open-submit]').forEach((button) => button.addEventListener('click', open));
    root.querySelectorAll('[data-mm-close-submit]').forEach((button) => button.addEventListener('click', close));

    // The dedicated photo pages (templates/metaobject/world_photo) are a
    // separate template with no submit dialog of their own, so their button
    // links here. #send-photo means "open the form", where the pre-existing
    // #share is only the anchor for the CTA section -- keeping them separate
    // means an ordinary in-page jump to #share does not pop a dialog at anyone.
    if (window.location.hash === '#send-photo' && dialog) {
      const shareSection = root.querySelector('#share') || root.querySelector('[data-mm-share-moments]');
      shareSection?.scrollIntoView();
      showDialog(dialog, root.querySelector('[data-mm-open-submit]'));
    }

    const renderPredictions = (items) => {
      predictions.replaceChildren();
      items.forEach((item) => {
        const button = createElement('button', { type: 'button', role: 'option' }, item.text);
        button.addEventListener('click', async () => {
          placeSearch.value = item.text;
          placeId.value = item.placeId;
          predictions.hidden = true;
          placeSearch.setAttribute('aria-expanded', 'false');
          try {
            await fetch(`${proxyBase}/google/select`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ placeId: item.placeId, sessionToken: token }) });
          } finally { token = sessionToken(); }
        }, { once: true });
        predictions.append(button);
      });
      if (items.length) predictions.append(createElement('small', { class: 'mm-world__google-attribution' }, i18n.googleAttribution));
      predictions.hidden = items.length === 0;
      placeSearch.setAttribute('aria-expanded', String(items.length > 0));
    };

    const autocomplete = debounce(async () => {
      placeId.value = '';
      const input = placeSearch.value.trim();
      if (input.length < 3) return renderPredictions([]);
      abortController?.abort(); abortController = new AbortController();
      try {
        const response = await fetch(`${proxyBase}/google/autocomplete`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: abortController.signal,
          body: JSON.stringify({ input, sessionToken: token })
        });
        const payload = await response.json();
        renderPredictions(response.ok ? payload.suggestions || [] : []);
      } catch (error) { if (error.name !== 'AbortError') renderPredictions([]); }
    }, 250);
    placeSearch?.addEventListener('input', autocomplete);

    // The geolocate() handler was removed along with its button. It called
    // navigator.geolocation.getCurrentPosition and stored the raw coordinates,
    // but never turned them into a place name, so the visitor saw nothing
    // happen. No geolocation permission is requested anywhere any more.

    const submit = async (event) => {
      event.preventDefault();
      errors.hidden = true; errors.textContent = '';
      if (!form.reportValidity()) return;
      if (!placeId.value) { errors.textContent = i18n.choosePrediction; errors.hidden = false; placeSearch.focus(); return; }
      const button = form.querySelector('[data-mm-submit-button]');
      button.disabled = true;
      const data = new FormData(form);
      const photo = data.get('photo');
      if (photo instanceof File && photo.size) data.set('photo', await compressImage(photo));
      try {
        const response = await fetch(form.action, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
        const payload = await response.json();
        if (!response.ok) {
          const message = Object.values(payload.errors || {}).filter(Boolean).join(' ') || payload.error || i18n.genericError;
          const failure = new Error(message);
          failure.isTurnstileFailure = payload.code === 'turnstile_failed' || payload.error === TURNSTILE_FAILURE_MESSAGE;
          throw failure;
        }
        // Success only: a file input can't be repopulated by JS, so the
        // error path below must never call form.reset() or the customer's
        // uploaded photo is destroyed.
        form.hidden = true; formHead.hidden = true; success.hidden = false; success.focus(); form.reset(); placeId.value = ''; token = sessionToken();
      } catch (error) {
        // Token is single-use and ~300s; reset on ANY failed submit so the
        // next attempt carries a fresh one instead of retrying a dead token.
        window.turnstile?.reset();
        errors.textContent = error.isTurnstileFailure ? (i18n.turnstileExpired || error.message) : (error.message || i18n.genericError);
        errors.hidden = false;
      }
      finally { button.disabled = false; }
    };
    form?.addEventListener('submit', submit);
    const backdrop = (event) => { if (event.target === dialog) close(); };
    dialog?.addEventListener('click', backdrop);
    cleanup.push(() => {
      abortController?.abort();
      root.querySelectorAll('[data-mm-open-submit]').forEach((button) => button.removeEventListener('click', open));
      root.querySelectorAll('[data-mm-close-submit]').forEach((button) => button.removeEventListener('click', close));
      placeSearch?.removeEventListener('input', autocomplete);
      form?.removeEventListener('submit', submit); dialog?.removeEventListener('click', backdrop);
    });
  }

  function initialise(root) {
    if (!root || initialised.has(root)) return;
    const cleanup = [];
    const sightings = readJson(root, '[data-mm-sightings]', []);
    const i18n = readJson(root, '[data-mm-i18n]', {});
    const countries = new Set(sightings.map((item) => item.country).filter(Boolean));
    const continents = new Set(sightings.map((item) => item.continent).filter(Boolean));
    const countryStat = root.querySelector('[data-mm-stat-countries]');
    const continentStat = root.querySelector('[data-mm-stat-continents]');
    if (countryStat) countryStat.textContent = countries.size;
    if (continentStat) continentStat.textContent = continents.size;
    ensureSightingCards(root, sightings, i18n);
    setupControlSystem(root);
    setupShareMoments(root, cleanup);
    const openDetail = setupDetails(root, sightings, i18n, cleanup);
    setupFilters(root, i18n, cleanup);
    setupGlobe(root, sightings, openDetail, i18n, cleanup);
    setupSubmission(root, i18n, cleanup);
    initialised.set(root, cleanup);
  }

  function destroy(root) {
    (initialised.get(root) || []).forEach((callback) => callback());
    initialised.delete(root);
  }

  const boot = (scope = document) => scope.querySelectorAll('[data-mm-world]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true }); else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-mm-world]').forEach(destroy));
})();
